import React, { useState } from 'react';
import { Sparkles, X, Check } from 'lucide-react';

// Each transition has an animated CSS preview that loops, so the user sees
// what the effect actually looks like before applying.
const TRANSITIONS: { id: string; label: string; previewCss: React.CSSProperties; keyframes: string }[] = [
  { id: 'cut',   label: 'Sem',   previewCss: {}, keyframes: '' },
  { id: 'fade',  label: 'Fade',  previewCss: { animation: 'tx-fade 2.4s ease-in-out infinite' },
    keyframes: '@keyframes tx-fade { 0%,15% { opacity: 0 } 50%,100% { opacity: 1 } }' },
  { id: 'slide', label: 'Slide', previewCss: { animation: 'tx-slide 2.4s ease-in-out infinite' },
    keyframes: '@keyframes tx-slide { 0%,15% { transform: translateX(-100%) } 50%,100% { transform: translateX(0) } }' },
  { id: 'wipe',  label: 'Wipe',  previewCss: { animation: 'tx-wipe 2.4s ease-in-out infinite' },
    keyframes: '@keyframes tx-wipe { 0%,15% { clip-path: inset(0 100% 0 0) } 50%,100% { clip-path: inset(0 0 0 0) } }' },
  { id: 'zoom',  label: 'Zoom',  previewCss: { animation: 'tx-zoom 2.4s ease-out infinite' },
    keyframes: '@keyframes tx-zoom { 0%,15% { transform: scale(0.2); opacity: 0 } 50%,100% { transform: scale(1); opacity: 1 } }' },
  { id: 'blur',  label: 'Blur',  previewCss: { animation: 'tx-blur 2.4s ease-out infinite' },
    keyframes: '@keyframes tx-blur { 0%,15% { filter: blur(20px); opacity: 0 } 50%,100% { filter: blur(0); opacity: 1 } }' },
  { id: 'spin',  label: 'Spin',  previewCss: { animation: 'tx-spin 2.4s ease-out infinite' },
    keyframes: '@keyframes tx-spin { 0%,15% { transform: rotate(-180deg) scale(0.5); opacity: 0 } 50%,100% { transform: rotate(0) scale(1); opacity: 1 } }' },
  { id: 'flip',  label: 'Flip',  previewCss: { animation: 'tx-flip 2.4s ease-out infinite' },
    keyframes: '@keyframes tx-flip { 0%,15% { transform: rotateY(90deg); opacity: 0 } 50%,100% { transform: rotateY(0); opacity: 1 } }' },
  { id: 'iris',  label: 'Iris',  previewCss: { animation: 'tx-iris 2.4s ease-out infinite' },
    keyframes: '@keyframes tx-iris { 0%,15% { clip-path: circle(0% at 50% 50%) } 50%,100% { clip-path: circle(75% at 50% 50%) } }' }
];

const ALL_KEYFRAMES = TRANSITIONS.map(t => t.keyframes).filter(Boolean).join('\n');

interface Props {
  value: string;
  duration: number; // seconds
  onChange: (transition: string) => void;
  onDurationChange: (duration: number) => void;
}

// Compact trigger button — opens a modal with all transitions previewed.
const TransitionPicker: React.FC<Props> = ({ value, duration, onChange, onDurationChange }) => {
  const [open, setOpen] = useState(false);
  const current = TRANSITIONS.find(t => t.id === value) || TRANSITIONS[0];

  return (
    <div>
      <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Transição</label>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary-500/50 text-left transition"
      >
        <Sparkles className="w-3.5 h-3.5 text-primary-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">
            {current.label}
            {value !== 'cut' && (
              <span className="ml-1 text-[10px] font-normal text-gray-500 tabular-nums">· {duration.toFixed(1)}s</span>
            )}
          </p>
        </div>
        <span className="text-[10px] text-primary-600 dark:text-primary-400 font-semibold shrink-0">Trocar</span>
      </button>

      {open && (
        <TransitionModal
          value={value}
          duration={duration}
          onChange={onChange}
          onDurationChange={onDurationChange}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
};

// Full picker — only mounted when the modal is open.
const TransitionModal: React.FC<Props & { onClose: () => void }> = ({
  value, duration, onChange, onDurationChange, onClose
}) => {
  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-gray-950/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <style>{ALL_KEYFRAMES}</style>
      <div
        className="w-full max-w-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary-500" /> Escolher transição
          </h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Grid */}
        <div className="p-4 overflow-y-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {TRANSITIONS.map(tr => {
              const active = value === tr.id;
              return (
                <button
                  key={tr.id}
                  onClick={() => onChange(tr.id)}
                  className={`relative rounded-lg overflow-hidden border-2 transition group ${
                    active
                      ? 'border-primary-500 ring-2 ring-primary-500/40'
                      : 'border-gray-200 dark:border-gray-700 hover:border-primary-500/50'
                  }`}
                  title={tr.label}
                >
                  <div className="aspect-video bg-gradient-to-br from-gray-900 to-gray-700 overflow-hidden relative">
                    <div
                      className="absolute inset-0 bg-gradient-to-br from-cyan-400 via-fuchsia-500 to-amber-400"
                      style={tr.previewCss}
                    />
                    {tr.id === 'cut' && (
                      <div className="absolute inset-0 flex items-center justify-center text-white/60 text-xs">sem efeito</div>
                    )}
                    {active && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary-500 text-white inline-flex items-center justify-center shadow">
                        <Check className="w-3 h-3" />
                      </div>
                    )}
                  </div>
                  <div className={`px-2 py-1.5 text-xs font-semibold text-center ${
                    active ? 'bg-primary-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}>{tr.label}</div>
                </button>
              );
            })}
          </div>

          {/* Duration */}
          <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-800">
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Duração: <span className="text-primary-600 dark:text-primary-400 tabular-nums">{duration.toFixed(1)}s</span>
            </label>
            <input
              type="range" min={0.1} max={3} step={0.1} value={duration}
              onChange={(e) => onDurationChange(Number(e.target.value))}
              className="w-full"
              disabled={value === 'cut'}
            />
            {value === 'cut' && <p className="text-[11px] text-gray-500 mt-1">Sem transição não tem duração.</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition"
          >
            Pronto
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransitionPicker;
